<script setup lang="ts">
import type Address from '@/components/auth/register/AddressFields.vue';
import type Button from '@/components/auth/verification/Box/Proceed.vue';

const { $containment } = useNuxtApp();
const { t } = useTexts();
const isLoading = ref(false);
const { openToaster } = useLayoutStore();
const { registerData } = storeToRefs(useRegisterStore());
const { fetchUserProfile } = useUserData();
const { isCaptchaRegisterEnabled } = useCaptcha();

const addressFieldsRef = ref<InstanceType<typeof Address> | null>(null);
const proceedButtonRef = ref<InstanceType<typeof Button> | null>(null);

const antiRobotToken = computed<string>(
  () => proceedButtonRef.value?.antiRobotToken ?? '',
);

const isProceedDisabled = computed(() => {
  if (isCaptchaRegisterEnabled.value && !antiRobotToken.value) {
    return true;
  }

  return (
    addressFieldsRef.value?.hasError || !addressFieldsRef.value?.anyChanges
  );
});

async function onAddressInfoFormSubmit() {
  if (isProceedDisabled.value) return;
  isLoading.value = true;

  try {
    const { ApiService } = useApiService();

    type SubmitData = {
      zipcode: string;
      address: string;
      city: string;
      state: string;
      country: string;
      captcha_token: string;
    };

    const submitData: SubmitData = {
      zipcode: registerData.value.zipcode,
      address: registerData.value.address,
      city: registerData.value.city,
      state: registerData.value.state,
      country: registerData.value.country_data,
      captcha_token: antiRobotToken.value,
    };

    const res = await ApiService.patch('/bff/users/update-pending-data', {
      address: submitData,
    });

    if (res.status.toString().startsWith('20') && res.data.success) {
      openToaster({
        message: t('account.user_data.update_success'),
        type: 'success',
      });

      await fetchUserProfile({ force: true });

      await $containment.solve({
        name: 'address',
        customValidator: () => res.data.success,
      });
    } else {
      handleError(res, 'auth.register.error.response_error');
    }
  } catch (err) {
    handleError(err, 'auth.register.error.response_error');
  } finally {
    isLoading.value = false;
  }
}

function handleError(err: any, fallbackMessageKey: string) {
  const errorMessage = err?.data?.message || t(fallbackMessageKey);

  openToaster({
    message: errorMessage,
    type: 'error',
  });
}
</script>

<template>
  <AuthVerificationBox :is-loading="isLoading">
    <form class="registerFields px-0 md:px-0">
      <AuthRegisterAddressFields
        ref="addressFieldsRef"
        get-values-from-user
      />

      <AuthVerificationBoxProceed
        ref="proceedButtonRef"
        class="mt-1"
        :disabled="isProceedDisabled"
        @submit="onAddressInfoFormSubmit"
      />
    </form>
  </AuthVerificationBox>
</template>
